import { Injectable, NgZone, signal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import {
  EstadoConexionUsuario,
  InternalUserPresence,
  ModoEstadoConexionUsuario,
} from '../../models/internal-chat.model';
import { InternalChatService } from '../api/internal-chat.service';
import { SseService } from '../api/sse.service';
import { ConfigurationService } from '../api/configuration.service';

export interface PresenceStatusTransition {
  username: string;
  previousStatus: EstadoConexionUsuario;
  status: EstadoConexionUsuario;
}

@Injectable({
  providedIn: 'root',
})
export class UserPresenceActivityService {
  readonly estadoConexion = signal<EstadoConexionUsuario>('CONECTADO');
  readonly modoEstadoConexion = signal<ModoEstadoConexionUsuario>('AUTOMATICO');
  readonly username = signal<string>('');
  readonly confirmedEstadoConexion = signal<EstadoConexionUsuario | null>(null);
  private readonly presenceTransitionSubject = new Subject<PresenceStatusTransition>();
  readonly presenceTransition$ = this.presenceTransitionSubject.asObservable();
  private readonly defaultInactivityLimitMs = 5 * 60 * 1000;
  private inactivityLimitMs = this.defaultInactivityLimitMs;
  private readonly onlineUpdateThrottleMs = 30000;
  private readonly activityEvents = [
    'mousemove',
    'mousedown',
    'keydown',
    'touchstart',
    'pointerdown',
  ];

  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private lastOnlineUpdateAt = 0;
  private started = false;
  private currentStatus: EstadoConexionUsuario = 'CONECTADO';
  private currentModoEstadoConexion: ModoEstadoConexionUsuario = 'AUTOMATICO';
  private pendingPresenceByUsername = new Map<string, InternalUserPresence>();
  private lastEmittedTransitionSignature: string | null = null;
  private lastActivityAt = 0;
  private updateSubscription: Subscription | null = null;
  private configurationSubscription: Subscription | null = null;
  private automaticUpdatesBlocked = false;
  private automaticAwayPending = false;
  private returnAfterAutomaticAway = false;
  private returnSourceAfterAutomaticAway = 'activity';

  private readonly handleActivity = (event: Event) => this.onUserActivity(false, event.type);
  private readonly handleWindowFocus = () => {
    if (!document.hidden) this.onUserActivity(false, 'focus');
  };
  private readonly handleVisibilityChange = () => {
    if (!document.hidden) this.onUserActivity(false, 'visibilitychange');
  };

  constructor(
    private internalChatService: InternalChatService,
    private ngZone: NgZone,
    private sseService: SseService,
    private configurationService: ConfigurationService,
  ) {
    this.sseService.internalUserPresenceReceived.subscribe((presence) =>
      this.applyOwnPresence(presence),
    );
    this.sseService.internalUsersPresenceSnapshotReceived.subscribe((snapshot) => {
      if (!this.username()) {
        for (const presence of snapshot || []) {
          if (presence?.username) this.bufferPresence(presence);
        }
        return;
      }
      const ownPresence = (snapshot || []).find(
        (presence) => presence.username === this.username(),
      );
      if (ownPresence) this.applyOwnPresence(ownPresence);
    });
  }

  setOwnUsername(username: string): void {
    const normalizedUsername = (username || '').trim();
    if (normalizedUsername !== this.username()) {
      this.confirmedEstadoConexion.set(null);
      this.lastEmittedTransitionSignature = null;
    }
    this.username.set(normalizedUsername);
    const pendingPresence = this.pendingPresenceByUsername.get(normalizedUsername);
    if (pendingPresence) {
      this.pendingPresenceByUsername.delete(normalizedUsername);
      this.applyOwnPresence(pendingPresence);
    }
  }

  start(): void {
    if (this.started || !this.hasActiveSession()) {
      return;
    }

    this.started = true;
    this.automaticUpdatesBlocked = false;
    this.configurationSubscription?.unsubscribe();
    this.configurationSubscription = this.configurationService.getPresenciaInactividadConfig().subscribe({
      next: (config) => {
        this.setInactivityLimitMinutes(config.minutosParaAusente);
        this.startActivityTracking();
      },
      error: () => this.startActivityTracking(),
    });
  }

  stop(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    for (const eventName of this.activityEvents) {
      window.removeEventListener(eventName, this.handleActivity);
    }
    window.removeEventListener('focus', this.handleWindowFocus);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    this.clearInactivityTimer();
    this.automaticAwayPending = false;
    this.returnAfterAutomaticAway = false;
    this.updateSubscription?.unsubscribe();
    this.updateSubscription = null;
    this.configurationSubscription?.unsubscribe();
    this.configurationSubscription = null;
  }

  setInactivityLimitMinutes(minutes: number): void {
    if (!Number.isInteger(minutes) || minutes < 1 || minutes > 1440) {
      return;
    }

    this.inactivityLimitMs = minutes * 60 * 1000;
    if (this.started) {
      this.resetInactivityTimer();
    }
  }

  setManualStatus(
    status: EstadoConexionUsuario,
    modo: ModoEstadoConexionUsuario = 'MANUAL',
  ): void {
    this.currentStatus = status;
    this.currentModoEstadoConexion = modo;
    this.estadoConexion.set(status);
    this.modoEstadoConexion.set(modo);
    this.lastOnlineUpdateAt = status === 'CONECTADO' ? Date.now() : 0;
    this.resetInactivityTimer();
  }

  syncStatus(
    status: EstadoConexionUsuario,
    modo?: ModoEstadoConexionUsuario,
    previousStatus?: EstadoConexionUsuario | null,
    transitionTimestamp?: string,
  ): void {
    this.currentStatus = status;
    if (modo) {
      this.currentModoEstadoConexion = modo;
    }
    this.estadoConexion.set(this.currentStatus);
    this.modoEstadoConexion.set(this.currentModoEstadoConexion);
    this.confirmStatus(status, previousStatus, transitionTimestamp);
    if (status === 'CONECTADO' && modo === 'AUTOMATICO' && this.started) {
      this.resetInactivityTimer();
    }
  }

  isBusy(): boolean {
    return this.currentStatus === 'OCUPADO';
  }

  private onUserActivity(forceOnlineUpdate = false, source = 'activity'): void {
    if (!this.started || !this.hasActiveSession()) {
      this.stop();
      return;
    }

    const now = Date.now();
    const inactivityReached = this.currentStatus === 'CONECTADO' &&
      this.currentModoEstadoConexion === 'AUTOMATICO' &&
      this.lastActivityAt > 0 &&
      now - this.lastActivityAt >= this.inactivityLimitMs;

    this.lastActivityAt = now;
    this.resetInactivityTimer();

    if (this.automaticAwayPending) {
      this.returnAfterAutomaticAway = true;
      this.returnSourceAfterAutomaticAway = source;
      return;
    }

    if (inactivityReached) {
      this.markAutomaticallyAway('inactivity-timeout', true, source);
      return;
    }

    if (
      this.currentStatus !== 'CONECTADO' ||
      forceOnlineUpdate ||
      Date.now() - this.lastOnlineUpdateAt >= this.onlineUpdateThrottleMs
    ) {
      this.updateStatus('CONECTADO', source);
    }
  }

  private isPageInactive(): boolean {
    return document.hidden ||
      (typeof document.hasFocus === 'function' && !document.hasFocus());
  }

  private markAutomaticallyAway(
    source: string,
    returnWhenConfirmed = false,
    returnSource = 'activity',
  ): void {
    if (!this.started || !this.hasActiveSession()) {
      this.stop();
      return;
    }

    if (
      this.automaticAwayPending ||
      this.currentStatus !== 'CONECTADO' ||
      this.currentModoEstadoConexion !== 'AUTOMATICO'
    ) {
      return;
    }

    this.automaticAwayPending = true;
    this.returnAfterAutomaticAway = returnWhenConfirmed;
    this.returnSourceAfterAutomaticAway = returnSource;
    this.updateStatus(
      'AUSENTE',
      source,
      () => {
        this.automaticAwayPending = false;
        if (!this.returnAfterAutomaticAway) return;

        const activitySource = this.returnSourceAfterAutomaticAway;
        this.returnAfterAutomaticAway = false;
        if (this.started && this.hasActiveSession()) {
          this.updateStatus('CONECTADO', activitySource);
        }
      },
      () => {
        this.automaticAwayPending = false;
        this.returnAfterAutomaticAway = false;
      },
    );
  }

  private startActivityTracking(): void {
    if (!this.started) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      for (const eventName of this.activityEvents) {
        window.addEventListener(eventName, this.handleActivity, {
          passive: true,
        });
      }
      window.addEventListener('focus', this.handleWindowFocus);
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    });

    this.lastActivityAt = Date.now();
    this.resetInactivityTimer();
    if (!this.isPageInactive()) this.onUserActivity(true, 'start');
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();

    if (!this.started) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const elapsedMs = this.lastActivityAt ? Date.now() - this.lastActivityAt : 0;
      const remainingMs = Math.max(0, this.inactivityLimitMs - elapsedMs);
      this.inactivityTimer = setTimeout(() => {
        const elapsedMs = this.lastActivityAt ? Date.now() - this.lastActivityAt : this.inactivityLimitMs;
        if (elapsedMs < this.inactivityLimitMs) {
          this.resetInactivityTimer();
          return;
        }
        this.markAutomaticallyAway('inactivity-timeout');
      }, remainingMs);
    });
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private updateStatus(
    status: EstadoConexionUsuario,
    source = 'unknown',
    onConfirmed?: () => void,
    onRejected?: () => void,
  ): void {
    if (this.automaticUpdatesBlocked || !this.hasActiveSession()) {
      this.stop();
      onRejected?.();
      return;
    }

    // Si el usuario está en MANUAL mode, no sobrescribir el estado automáticamente
    if (
      this.currentModoEstadoConexion === 'MANUAL' &&
      (this.currentStatus === 'OCUPADO' || this.currentStatus === 'AUSENTE')
    ) {
      onRejected?.();
      return;
    }

    if (status === this.currentStatus && status !== 'CONECTADO') {
      onRejected?.();
      return;
    }

    this.currentStatus = status;
    this.estadoConexion.set(status);
    if (status === 'CONECTADO') {
      this.lastOnlineUpdateAt = Date.now();
    }

    this.updateSubscription?.unsubscribe();
    this.ngZone.run(() => {
      this.updateSubscription = this.internalChatService
        .updatePresenceStatus({ estadoConexion: status })
        .subscribe({
          next: (presence: InternalUserPresence) => {
            if (presence.estadoConexion) {
              this.applyConfirmedPresence(presence);
              if (presence.estadoConexion === status) onConfirmed?.();
              else onRejected?.();
            } else {
              onRejected?.();
            }
          },
          error: (error) => {
            if (error?.status === 403) {
              this.automaticUpdatesBlocked = true;
              this.stop();
              console.warn('Actualización automática de presencia detenida por permisos o CSRF');
              return;
            }
            if (this.confirmedEstadoConexion()) {
              this.currentStatus = this.confirmedEstadoConexion()!;
              this.estadoConexion.set(this.currentStatus);
            }
            console.error('Error al actualizar presencia por actividad:', error);
            onRejected?.();
          },
        });
    });
  }

  private hasActiveSession(): boolean {
    const token = sessionStorage.getItem('authenticated');
    return !!token && token !== 'undefined';
  }

  private applyOwnPresence(presence: InternalUserPresence): void {
    if (!presence?.username) return;
    const ownUsername = this.username();
    if (!ownUsername) {
      this.bufferPresence(presence);
      return;
    }
    if (presence.username !== ownUsername) return;
    this.applyConfirmedPresence(presence);
  }

  private applyConfirmedPresence(presence: InternalUserPresence): void {
    if (!this.username() && presence.username) this.bufferPresence(presence);
    if (presence.estadoConexion) this.currentStatus = presence.estadoConexion;
    if (presence.modoEstadoConexion) this.currentModoEstadoConexion = presence.modoEstadoConexion;
    this.estadoConexion.set(this.currentStatus);
    this.modoEstadoConexion.set(this.currentModoEstadoConexion);
    this.confirmStatus(
      presence.estadoConexion || this.currentStatus,
      presence.estadoConexionAnterior,
      presence.timestamp,
    );
  }

  private confirmStatus(
    status: EstadoConexionUsuario,
    previousStatus?: EstadoConexionUsuario | null,
    transitionTimestamp?: string,
  ): void {
    const lastConfirmedStatus = this.confirmedEstadoConexion();
    const confirmedPreviousStatus = previousStatus ?? lastConfirmedStatus;
    this.confirmedEstadoConexion.set(status);
    const isUnobservedDisconnectedRecovery = status === 'CONECTADO' &&
      previousStatus === 'DESCONECTADO' &&
      lastConfirmedStatus !== null &&
      lastConfirmedStatus !== 'DESCONECTADO' &&
      lastConfirmedStatus !== 'AUSENTE';
    if (
      confirmedPreviousStatus &&
      confirmedPreviousStatus !== status &&
      this.username() &&
      !isUnobservedDisconnectedRecovery
    ) {
      const username = this.username();
      const signature = `${username}|${confirmedPreviousStatus}|${status}|${transitionTimestamp || ''}`;
      if (signature !== this.lastEmittedTransitionSignature) {
        this.lastEmittedTransitionSignature = signature;
        this.presenceTransitionSubject.next({
          username,
          previousStatus: confirmedPreviousStatus,
          status,
        });
      }
    }
  }

  private bufferPresence(presence: InternalUserPresence): void {
    const pending = this.pendingPresenceByUsername.get(presence.username);
    const pendingHasTransition = !!pending?.estadoConexionAnterior &&
      pending.estadoConexionAnterior !== pending.estadoConexion;
    const incomingHasTransition = !!presence.estadoConexionAnterior &&
      presence.estadoConexionAnterior !== presence.estadoConexion;
    const confirmedTransition = pendingHasTransition &&
      pending.estadoConexion === presence.estadoConexion &&
      !incomingHasTransition
      ? pending.estadoConexionAnterior
      : presence.estadoConexionAnterior;
    this.pendingPresenceByUsername.set(presence.username, {
      ...presence,
      estadoConexionAnterior: confirmedTransition,
    });
  }
}
